import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

interface ProgressBarProps {
  strings: string[];
  onComplete?: () => void;
  visibleIndex: number;
  setVisibleIndex: React.Dispatch<React.SetStateAction<number>>;
}


const Wrapper = styled.div<{ progress: number }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  margin-top: 20px;
  margin-bottom: 40px;

  position: relative;
  max-width: 100%;
  height: 50px;
  border: 1px solid #E7F0FF;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0px 4px 4px 0px #00000014 inset;
  background-color: #fff;
  .progressbar-complete {
    position: absolute;
    left: 0;
    top: 0px;
    height: 100%;
    background: linear-gradient(270deg, rgba(11, 109, 255, 0.6) 10.11%, #0B6DFF 92.13%);
    border-radius: 100px;
    z-index: 2;
    .progressbar-liquid {
      z-index: 1;
      width: 70px;
      height: 70px;
      position: absolute;
      right: -5px;
      top: -10px;
      border-radius: 40%;
    }
  }

  .progress {
    position: relative;
    z-index: 3;
  }
`;

const Container = styled.div`
  padding:0 30px;
  width: 100%;
  box-sizing: border-box;
`;

const TOTAL_DURATION = 9000; // 9 seconds in milliseconds
const PHASE_1_DURATION = 1500; // 1.5 seconds for 0-30%
const PHASE_2_DURATION = 3000; // 3 seconds for 30-60%
const PHASE_3_DURATION = 4500; // 4.5 seconds for 60-100%

const LoadingBar: React.FC<ProgressBarProps> = ({ strings, onComplete, visibleIndex, setVisibleIndex }) => {
  const [targetProgress, setTargetProgress] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);

  // Initialize start time when component mounts
  useEffect(() => {
    setStartTime(Date.now());
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (onComplete) {
        onComplete();
      }
    }, TOTAL_DURATION);

    return () => clearTimeout(timer);
  }, [onComplete]);

  // Calculate progress based on elapsed time
  useEffect(() => {
    if (startTime === null) return;

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      
      if (elapsed >= TOTAL_DURATION) {
        setTargetProgress(100);
        setVisibleIndex(strings.length);
        return;
      }

      let progress = 0;
      let newPhase = 0;
      
      if (elapsed <= PHASE_1_DURATION) {
        // Phase 1: 0-30% in 1.5s
        progress = (elapsed / PHASE_1_DURATION) * 30;
        newPhase = 0;
      } else if (elapsed <= PHASE_1_DURATION + PHASE_2_DURATION) {
        // Phase 2: 30-60% in 3s
        const phase2Elapsed = elapsed - PHASE_1_DURATION;
        progress = 30 + (phase2Elapsed / PHASE_2_DURATION) * 30;
        newPhase = 1;
      } else {
        // Phase 3: 60-100% in 4.5s
        const phase3Elapsed = elapsed - PHASE_1_DURATION - PHASE_2_DURATION;
        progress = 60 + (phase3Elapsed / PHASE_3_DURATION) * 40;
        newPhase = 2;
      }

      setTargetProgress(progress);
      if (newPhase !== visibleIndex) {
        setVisibleIndex(newPhase);
      }
      
    };

    updateProgress();
    const interval = setInterval(updateProgress, 16); // ~60fps

    return () => clearInterval(interval);
  }, [startTime, strings.length, setVisibleIndex]);

  return (
    <Container>
      <Wrapper progress={targetProgress}>
        <div className="progressbar-container">
          <div className="progressbar-complete" style={{ width: `${targetProgress}%` }}>
            <div className="progressbar-liquid"></div>
          </div>
        </div>
      </Wrapper>
    </Container>
  );
};

export default LoadingBar;
